import { FC, memo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
// Import Types
import type { IButtonProps } from '@geneui/components/lib/atoms/Button';
import type { NavigationItemProps } from './types';
// Import Constants
import { BLOCKED_BY_SUBSCRIPTION_CLASSNAME, l } from 'constants/common';
// Import UI Components
import { Button, Icon, PopoverV2, Tooltip } from '@geneui/components';
// Import Components
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
// Import Context
import { useActiveStepActions } from './context';
//Import Styles
import styles from './stepper.module.scss';

const StepperNavigation: FC<NavigationItemProps> = ({ isLoading, step, onStepChange }) => {
    const {
        id,
        icon,
        label,
        badge,
        tooltipText = '',
        isActive,
        isMounted,
        isStepValid,
        isStepInvalid,
        isPending,
        isDisabled,
    } = step;
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);

    const { handleClear, handleReset } = useActiveStepActions();

    const hasActiveStepActions = !!handleClear || !!handleReset;

    const togglePopover = () => setIsOpen((prev) => !prev);

    const onClear = () => {
        handleClear?.();
        togglePopover();
    };

    const onReset = () => {
        handleReset?.();
        togglePopover();
    };

    const buttonProps: IButtonProps = {
        className: styles.navigationStepItemActionButton,
        color: 'default',
        appearance: 'minimal',
        flexibility: 'full-width',
        itemsDirection: 'start',

        disabled: isLoading,
    };

    const contentNode = (
        <div
            className={classNames(styles.navigationStepItem, {
                [styles.active]: isActive,
                [styles.valid]: isMounted && isStepValid,
                [styles.invalid]: isMounted && isStepInvalid,
                [styles.pending]: isPending,
                [BLOCKED_BY_SUBSCRIPTION_CLASSNAME]: isDisabled,
            })}
            onClick={isDisabled ? undefined : () => onStepChange(id)}
        >
            <div className={styles.navigationStepItemIcon}>
                <Icon type={isPending ? 'bc-icon-loader' : icon} />
            </div>
            <div className={styles.navigationStepItemLabelContainer}>
                <div className={styles.navigationStepItemLabel}>{t(label)}</div>
                {badge && (
                    <div className={styles.badgeContainer}>
                        <div className={styles.badge} style={{ backgroundColor: badge.color }}>
                            {t(badge.label)}
                        </div>
                    </div>
                )}
            </div>

            {isActive && hasActiveStepActions && !isDisabled && (
                <div className={styles.navigationStepItemActions}>
                    <PopoverV2
                        isOpen={isOpen}
                        toggleHandler={togglePopover}
                        behave="toggle"
                        position="bottom"
                        align="end"
                        extendTargetWidth={false}
                        Content={
                            <div className={styles.navigationStepItemActionsPopover}>
                                {handleReset && (
                                    <Button {...buttonProps} icon="bc-icon-reset" onClick={onReset}>
                                        {t(l.Reset)}
                                    </Button>
                                )}
                                {handleClear && (
                                    <Button {...buttonProps} icon="bc-icon-trash" onClick={onClear}>
                                        {t(l.Clear)}
                                    </Button>
                                )}
                            </div>
                        }
                    >
                        <Button icon="bc-icon-more-vertical" appearance="clean" />
                    </PopoverV2>
                </div>
            )}
        </div>
    );

    return isDisabled ? (
        <BlockedBySubscriptionInfo position="top">{contentNode}</BlockedBySubscriptionInfo>
    ) : (
        <Tooltip text={t(tooltipText)}>{contentNode}</Tooltip>
    );
};

export default memo(StepperNavigation);
